import { BACKOFFICE_MANAGER_URL, BASE_URL } from "../../request";
import ModuleTranslations from "./ModuleTranslations";
import { getTranslatedDefinition } from "../utils";
import {
  AVAILABILITY,
  BLOCKAGE,
  EXTRA_TYPE_VARIANT,
  GUEST_TYPE,
  RATE_TYPE_NEW,
  RESTRICTIONS_TYPE,
  StayDaysFilterType,
} from "../../Common/constants";
import { tickerValidator } from "../../globalVariables";
import Cookies from "js-cookie";
import { FormattedMessage } from "react-intl";
import React from "react";

export const EXTRA_TYPES_WITH_AVAILABILITY = [
  EXTRA_TYPE_VARIANT.ROOM_PER_DAY,
  EXTRA_TYPE_VARIANT.ROOM_SPECIFIC_DATE,
  EXTRA_TYPE_VARIANT.PERSON_PER_DAY,
  EXTRA_TYPE_VARIANT.PERSON_SPECIFIC_DATE,
  EXTRA_TYPE_VARIANT.UNIT_PER_DAY,
  EXTRA_TYPE_VARIANT.UNIT_SPECIFIC_DATE,
];

export const EXTRA_TYPES_WITH_STAY_DAYS_FILTER = [
  EXTRA_TYPE_VARIANT.ROOM_SPECIFIC_DATE,
  EXTRA_TYPE_VARIANT.PERSON_SPECIFIC_DATE,
  EXTRA_TYPE_VARIANT.UNIT_SPECIFIC_DATE,
];

const messages = {
  helpText: {
    id: "extras.images.helpText",
    defaultMessage: "Tamaño 1900x1080px",
  },
};

const ModuleDefinition = {
  name: "extras",
  entityName: "extras",
  restConfig: {
    baseURL: `${BACKOFFICE_MANAGER_URL}`,
    resetCacheURL: `${BASE_URL}`,
    create: {
      url: ({ propertyTicker }) => `/v1/${propertyTicker}/extra/legacy`,
      method: "post",
      resetCache: true,
      headers: {
        Authorization: Cookies.get("wb_access_token"),
      },
    },
    update: {
      url: ({ propertyTicker, id }) => `/v1/${propertyTicker}/extra/${id}`,
      method: "put",
      resetCache: true,
      headers: {
        Authorization: Cookies.get("wb_access_token"),
      },
    },
    fetch: {
      url: ({ propertyTicker, id }) =>
        id ? `/v1/${propertyTicker}/extra/${id}` : `/v1/${propertyTicker}/extra`,
      method: "get",
      headers: {
        Authorization: Cookies.get("wb_access_token"),
      },
    },
    delete: {
      url: ({ id }, hotelTicker) => `/v1/${hotelTicker}/extra/legacy/${id}`,
      method: "delete",
      resetCache: true,
      headers: {
        Authorization: Cookies.get("wb_access_token"),
      },
    },
    reorder: {
      url: ({ propertyTicker, id, afterId }) =>
        `/v1/${propertyTicker}/extra/${id}/reorder` +
        (afterId ? `?afterId=${encodeURIComponent(afterId)}` : ""),
      method: "put",
      resetCache: true,
      headers: {
        Authorization: Cookies.get("wb_access_token"),
      },
    },
  },
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "Translations",
    widget: "translations",
    entityProperty: "translations",
  },
  allowClone: true,
  sorting: null,
  reorder: true,
  allowImageEdit: true,
  fields: {
    list: [
      {
        fieldName: "active",
        displayName: "Active",
        widget: "singleSwitch",
        toggleAction: true,
        width: 60,
      },
      {
        fieldName: "ticker",
        displayName: "Ticker",
        width: 140,
      },
      {
        fieldName: "name",
        displayName: "Name",
        width: 200,
      },
      {
        fieldName: "type",
        displayName: "Type",
        className: "doubleLine",
        width: 200,
      },
      {
        fieldName: "rate",
        displayName: "Rate",
        width: 80,
      },
      {
        fieldName: "validity",
        displayName: "Validity(dates)",
        className: "doubleLine",
        width: 80,
      },
      {
        fieldName: "availability",
        displayName: "Availability",
        width: 120,
      },
      {
        fieldName: "blocking",
        displayName: "Block Validity",
        width: 80,
      },
      {
        fieldName: "lockType",
        displayName: "Blocking behavior",
        width: 80,
      },
      {
        fieldName: "notificationEmails",
        displayName: "Email",
        width: 120,
      },
      {
        fieldName: "minStay",
        displayName: "Min. Stay(d)",
        width: 80,
      },
      {
        fieldName: "maxStay",
        displayName: "Max. Stay(d)",
        width: 80,
      },
      {
        fieldName: "minNotice",
        displayName: "Min. Release(h)",
        width: 80,
      },
      {
        fieldName: "maxNotice",
        displayName: "Max. Release(h)",
        width: 80,
      },
      {
        fieldName: "promoCode",
        displayName: "Code",
        width: 120,
      },
    ],
    form: [
      {
        fieldName: "id",
        displayName: "id",
        widget: "hidden",
      },
      {
        fieldName: "propertyTicker",
        widget: "hidden",
      },
      {
        fieldName: "active",
        displayName: "Active",
        widget: "singleSwitch",
      },
      {
        fieldName: "ticker",
        displayName: "Ticker",
        widget: "input",
        readOnly: true,
        required: true,
        maxLength: 8,
        helpText: "Maximum characters: 8",
      },
      {
        translationsList: true,
      },
      {
        fieldName: "name",
        displayName: "Name",
        widget: "input",
        translated: true,
        required: true,
      },
      {
        fieldName: "description",
        displayName: "Description",
        widget: "richHTMLEditor",
        translated: true,
        required: true,
      },
      {
        fieldName: "ratesSection",
        displayName: "Rates",
        widget: "section",
      },
      {
        fieldName: "rateType",
        required: true,
        displayName: "Rates",
        widget: "selector",
        optionDisplay: {
          "": {
            label: "--",
          },
          [RATE_TYPE_NEW.CONSTANT]: {
            label: "Constant",
          },
          [RATE_TYPE_NEW.OWN]: {
            label: "Own",
          },
        },
        defaultValue: "",
      },
      {
        fieldName: "rateAmount",
        widget: "input",
        visible: ({ formValues }) => formValues.rateType === RATE_TYPE_NEW.CONSTANT,
        className: "negativeMarginSmall",
      },
      {
        fieldName: "type",
        displayName: "Type",
        widget: "selector",
        optionDisplay: {
          [EXTRA_TYPE_VARIANT.ROOM]: {
            label: "Per room",
          },
          [EXTRA_TYPE_VARIANT.ROOM_PER_DAY]: {
            label: "Per room/day",
          },
          [EXTRA_TYPE_VARIANT.ROOM_MANDATORY]: {
            label: "Per room (Mandatory)",
          },
          [EXTRA_TYPE_VARIANT.ROOM_PER_DAY_MANDATORY]: {
            label: "Per room/day (Mandatory)",
          },
          [EXTRA_TYPE_VARIANT.ROOM_SPECIFIC_DATE]: {
            label: "Per room/specific dates",
          },
          [EXTRA_TYPE_VARIANT.PERSON]: {
            label: "Per person",
          },
          [EXTRA_TYPE_VARIANT.PERSON_PER_DAY]: {
            label: "Per person/day",
          },
          [EXTRA_TYPE_VARIANT.PERSON_MANDATORY]: {
            label: "Per person (Mandatory)",
          },
          [EXTRA_TYPE_VARIANT.PERSON_PER_DAY_MANDATORY]: {
            label: "Per person/day (Mandatory)",
          },
          [EXTRA_TYPE_VARIANT.PERSON_SPECIFIC_DATE]: {
            label: "Per person/specific dates",
          },
          [EXTRA_TYPE_VARIANT.UNIT]: {
            label: "Per unit",
          },
          [EXTRA_TYPE_VARIANT.UNIT_PER_DAY]: {
            label: "Per unit/day",
          },
          [EXTRA_TYPE_VARIANT.UNIT_SPECIFIC_DATE]: {
            label: "Per unit/specific dates",
          },
        },
        defaultValue: EXTRA_TYPE_VARIANT.ROOM,
      },
      {
        fieldName: "stayDaysFilterType",
        displayName: "Apply to:",
        widget: "selector",
        optionDisplay: {
          [StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKOUT]: {
            label: "All stay days except check-out",
          },
          [StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKIN]: {
            label: "All stay days except check-in",
          },
          // TODO: Uncomment when the feature of checkout is implemented
          // [StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKIN_AND_CHECKOUT]: {
          //   label: "All stay days except check-in and check-out",
          // },
          [StayDaysFilterType.ONLY_CHECKIN]: {
            label: "Only check-in",
          },
          // [StayDaysFilterType.ONLY_CHECKOUT]: {
          //   label: "Only check-out",
          // },
        },
        visible: ({ formValues }) => EXTRA_TYPES_WITH_STAY_DAYS_FILTER.includes(formValues.type),
        defaultValue: StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKOUT,
      },
      {
        fieldName: "availabilityType",
        displayName: "Availability",
        widget: "selector",
        optionDisplay: {
          "": {
            label: "--",
          },
          [`AVAILABILITY_${AVAILABILITY.OWN}`]: {
            label: "Own",
          },
        },
        defaultValue: "",
        visible: ({ formValues }) => EXTRA_TYPES_WITH_AVAILABILITY.includes(formValues.type),
      },
      {
        fieldName: "blockingType",
        displayName: "Blocking",
        widget: "selector",
        optionDisplay: {
          "": {
            label: "--",
          },
          [BLOCKAGE.OWN]: {
            label: "Own",
          },
        },
        defaultValue: "",
      },
      {
        fieldName: "blockingValue",
        widget: "selector",
        transform: ({ entity, field }) => ({
          ...field,
          optionDisplay:
            entity &&
            entity.blockingValues &&
            entity.blockingValues.reduce((accum, it) => ({ ...accum, [it]: { label: it } }), {
              "": { label: "Choose inventory line" },
            }),
          className: "negativeMargin",
        }),
        defaultValue: "",
        visible: ({ formValues }) => formValues.blockingType === BLOCKAGE.SHARED,
        className: "negativeMarginSmall",
        required: ({ formValues }) => formValues.blockingType === BLOCKAGE.SHARED,
      },

      {
        fieldName: "lockType",
        displayName: "LockType",
        widget: "selector",
        optionDisplay: {
          [EXTRA_TYPE_VARIANT.LOCK_TYPE_STAY]: {
            label: "Per stay",
          },
          [EXTRA_TYPE_VARIANT.LOCK_TYPE_DAY]: {
            label: "Per day",
          },
        },
        defaultValue: EXTRA_TYPE_VARIANT.LOCK_TYPE_STAY,
      },
      {
        fieldName: "appliesToGuests",
        displayName: "Applies to",
        widget: "multicheckbox",
        className: "noMargin",
        required: true,
        visible: ({ formValues }) =>
          formValues.type === EXTRA_TYPE_VARIANT.PERSON ||
          formValues.type === EXTRA_TYPE_VARIANT.PERSON_MANDATORY ||
          formValues.type === EXTRA_TYPE_VARIANT.PERSON_PER_DAY ||
          formValues.type === EXTRA_TYPE_VARIANT.PERSON_PER_DAY_MANDATORY ||
          formValues.type === EXTRA_TYPE_VARIANT.PERSON_SPECIFIC_DATE,
        transform: ({ formValues, field }) => ({
          ...field,
          options: [
            {
              ticker: GUEST_TYPE.ADULT,
              name: "ADULT",
            },
            {
              ticker: GUEST_TYPE.CHILD,
              name: "CHILD",
            },
            {
              ticker: GUEST_TYPE.BABY,
              name: "BABY",
            },
            {
              ticker: GUEST_TYPE.TEENAGER,
              name: "JUNIOR",
            },
            {
              ticker: GUEST_TYPE.SENIOR,
              name: "SENIOR",
            },
          ],
        }),
      },
      {
        fieldName: "maxBookableUnits",
        displayName: "Max. Bookable Units",
        widget: "selector",
        optionDisplay: (() =>
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce(
            (accum, it) => ({ ...accum, [it]: { label: it } }),
            {}
          ))(),
        visible: ({ formValues }) =>
          formValues.type === EXTRA_TYPE_VARIANT.UNIT ||
          formValues.type === EXTRA_TYPE_VARIANT.UNIT_PER_DAY ||
          formValues.type === EXTRA_TYPE_VARIANT.UNIT_SPECIFIC_DATE,
        defaultValue: 1,
      },
      {
        fieldName: "codeSection",
        displayName: "Coupon / company code",
        widget: "section",
      },
      {
        fieldName: "promoCode",
        displayName: "Promo id",
        widget: "input",
      },
      {
        fieldName: "validitySection",
        displayName: "Validity",
        widget: "section",
      },
      {
        fieldName: "validFrom",
        displayName: "Start Date",
        widget: "datepicker",
        className: "noCloseIcon",
        dateFormat: "YYYY-MM-DD",
      },
      {
        fieldName: "validTo",
        displayName: "End Date",
        widget: "datepicker",
        className: "noCloseIcon",
        dateFormat: "YYYY-MM-DD",
      },
      {
        fieldName: "minStayType",
        displayName: "Min. Stay (days)",
        widget: "selector",
        optionDisplay: {
          [RESTRICTIONS_TYPE.NO_LIMIT]: {
            label: "Unlimited",
          },
          [RESTRICTIONS_TYPE.CONSTANT]: {
            label: "Constant",
          },
        },
        defaultValue: RESTRICTIONS_TYPE.NO_LIMIT,
      },
      {
        fieldName: "minStayValue",
        widget: "input",
        visible: ({ formValues }) => formValues.minStayType === RESTRICTIONS_TYPE.CONSTANT,
        className: "negativeMarginSmall",
        required: true,
      },
      {
        fieldName: "maxStayType",
        displayName: "Max. Stay (days)",
        widget: "selector",
        optionDisplay: {
          [RESTRICTIONS_TYPE.NO_LIMIT]: {
            label: "Unlimited",
          },
          [RESTRICTIONS_TYPE.CONSTANT]: {
            label: "Constant",
          },
        },
        defaultValue: RESTRICTIONS_TYPE.NO_LIMIT,
      },
      {
        fieldName: "maxStayValue",
        widget: "input",
        visible: ({ formValues }) => formValues.maxStayType === RESTRICTIONS_TYPE.CONSTANT,
        className: "negativeMarginSmall",
        required: true,
      },
      {
        fieldName: "minNoticeType",
        displayName: "Min. Notice (hours)",
        widget: "selector",
        optionDisplay: {
          [RESTRICTIONS_TYPE.NO_LIMIT]: {
            label: "Unlimited",
          },
          [RESTRICTIONS_TYPE.CONSTANT]: {
            label: "Constant",
          },
        },
        defaultValue: RESTRICTIONS_TYPE.NO_LIMIT,
      },
      {
        fieldName: "minNoticeValue",
        widget: "input",
        visible: ({ formValues }) => formValues.minNoticeType === RESTRICTIONS_TYPE.CONSTANT,
        className: "negativeMarginSmall",
        required: true,
      },
      {
        fieldName: "maxNoticeType",
        displayName: "Max. Notice (hours)",
        widget: "selector",
        optionDisplay: {
          [RESTRICTIONS_TYPE.NO_LIMIT]: {
            label: "Unlimited",
          },
          [RESTRICTIONS_TYPE.CONSTANT]: {
            label: "Constant",
          },
        },
        defaultValue: RESTRICTIONS_TYPE.NO_LIMIT,
      },
      {
        fieldName: "maxNoticeValue",
        widget: "input",
        visible: ({ formValues }) => formValues.maxNoticeType === RESTRICTIONS_TYPE.CONSTANT,
        className: "negativeMarginSmall",
        required: true,
      },
      {
        fieldName: "emailSection",
        displayName: "Email list",
        widget: "section",
      },
      {
        fieldName: "notificationEmails",
        displayName: "Booking confirmation recipients",
        widget: "input",
      },
      {
        fieldName: "roomsWithExtrasSection",
        displayName: "Rooms with extra enabled",
        widget: "section",
      },
      {
        fieldName: "appliesTo",
        widget: "smartSelector",
        transform: ({ formValues, field, entity }) => ({
          ...field,
          options: entity.inventories.map(inv => {
            inv.name =
              inv.accommodation.name +
              " / " +
              inv.configuration.name +
              " , " +
              inv.mealPlan.name +
              " , " +
              inv.condition.name;
            inv.filters = {
              accommodation: inv.accommodation,
              condition: inv.condition,
              configuration: inv.configuration,
              mealPlan: inv.mealPlan,
            };
            return inv;
          }),
          staticFilters: {
            configuration: entity.inventories.map(inv => {
              if (
                formValues.appliesToGuests.includes(GUEST_TYPE.ADULT) &&
                inv.configuration.adult > 0
              ) {
                return undefined;
              }
              if (
                formValues.appliesToGuests.includes(GUEST_TYPE.CHILD) &&
                inv.configuration.child > 0
              ) {
                return undefined;
              }
              if (
                formValues.appliesToGuests.includes(GUEST_TYPE.BABY) &&
                inv.configuration.baby > 0
              ) {
                return undefined;
              }
              if (
                formValues.appliesToGuests.includes(GUEST_TYPE.TEENAGER) &&
                inv.configuration.teenager > 0
              ) {
                return undefined;
              }
              if (
                formValues.appliesToGuests.includes(GUEST_TYPE.SENIOR) &&
                inv.configuration.senior > 0
              ) {
                return undefined;
              }
              return inv.configuration.ticker;
            }),
          },
        }),
        filterNames: ["Accommodation", "Conditions", "Configuration", "Meal Plan"],
      },
      {
        fieldName: "imageManagementSection",
        displayName: "Image management",
        widget: "section",
        tab: 1,
      },
      {
        fieldName: "multimedia",
        widget: "imageManager",
        tab: 1,
      },
      {
        fieldName: "infoMessage",
        widget: "custom",
        customRenderer: () => (
          <label className="text-warning" style={{ whiteSpace: "pre-line" }}>
            <FormattedMessage {...messages.helpText} />
          </label>
        ),
        tab: 1,
      },
      {
        fieldName: "oneTimePromoCode",
        widget: "oneTimePromoCode",
        transform: ({ field, formValues }) => ({
          ...field,
          propertyTickers: formValues.propertyTickers || [],
        }),
        tab: 2,
        visible: ({ formValues }) => formValues.id,
        entityType: "EXTRA",
      },
      {
        fieldName: "infoMessage",
        widget: "simpleText",
        value:
          "To activate single-use promocodes tab, you must save a promotion first and access it again to edit.",
        tab: 2,
        visible: ({ entity }) => !entity.id,
      },
    ],
  },
  validation: {
    ticker: tickerValidator,
    propertyTicker: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    appliesToGuests: {
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    validFrom: {
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    validTo: {
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    name: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    description: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    blockingValue: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    rateType: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    rateAmount: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    minStayValue: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 2,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    maxStayValue: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    minNoticeValue: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    maxNoticeValue: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    notificationEmails: {
      constraints: {
        emailList: {},
      },
    },
  },
};

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
